(() => {
  'use strict';

  if (window.ArticleEnhancements) {
    window.ArticleEnhancements.init(document);
    return;
  }

  const mobileMedia = window.matchMedia('(max-width: 749px)');
  const tocInstances = new Set();
  let progressFrame = null;

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const slugify = (value) => {
    const slug = value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug || 'article-section';
  };

  const getHeadingId = (heading, index, claimedIds) => {
    const existingId = heading.id.trim();

    if (
      existingId &&
      !claimedIds.has(existingId) &&
      document.getElementById(existingId) === heading
    ) {
      claimedIds.add(existingId);
      return existingId;
    }

    const baseId = slugify(heading.textContent) || `article-section-${index + 1}`;
    let candidate = baseId;
    let suffix = 2;

    while (claimedIds.has(candidate) || document.getElementById(candidate)) {
      candidate = `${baseId}-${suffix}`;
      suffix += 1;
    }

    heading.id = candidate;
    claimedIds.add(candidate);
    return candidate;
  };

  const setTocExpanded = (instance, expanded) => {
    instance.button.setAttribute('aria-expanded', String(expanded));
    instance.content.hidden = !expanded;
    instance.element.classList.toggle('is-open', expanded);
  };

  const applyTocViewportState = (instance) => {
    const collapseOnMobile = instance.element.dataset.mobileCollapsed === 'true';
    setTocExpanded(instance, !(mobileMedia.matches && collapseOnMobile));
  };

  const registerToc = (toc, button, content, list) => {
    const instance = { element: toc, button, content };
    tocInstances.add(instance);

    button.addEventListener('click', () => {
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      setTocExpanded(instance, !isExpanded);
    });

    list.addEventListener('click', (event) => {
      if (mobileMedia.matches && event.target instanceof Element && event.target.closest('a')) {
        setTocExpanded(instance, false);
      }
    });

    applyTocViewportState(instance);
    toc.hidden = false;
  };

  const buildToc = (toc) => {
    if (toc.dataset.tocReady === 'true') return;
    toc.dataset.tocReady = 'true';

    const article = toc.closest('[data-article-enhancements]');
    const content = article?.querySelector('.article-template__content');
    const list = toc.querySelector('[data-article-toc-list]');
    const button = toc.querySelector('[data-article-toc-toggle]');
    const tocContent = toc.querySelector('[data-article-toc-content]');

    if (!content || !list || !button || !tocContent) return;

    const selector = toc.dataset.includeH3 === 'true' ? 'h2, h3' : 'h2';
    const headings = Array.from(content.querySelectorAll(selector)).filter(
      (heading) => heading.textContent.trim().length > 0
    );
    const minimumHeadings = Math.max(Number.parseInt(toc.dataset.minHeadings, 10) || 2, 1);
    const designMode = toc.dataset.designMode === 'true';

    list.replaceChildren();

    if (headings.length < minimumHeadings) {
      if (designMode) {
        const item = document.createElement('li');
        item.className = 'article-template__toc-placeholder';
        item.textContent = `The table of contents will appear when the article has at least ${minimumHeadings} matching headings.`;
        list.append(item);
        registerToc(toc, button, tocContent, list);
      }
      return;
    }

    const claimedIds = new Set();
    let latestH2Item = null;
    let nestedList = null;

    headings.forEach((heading, index) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const headingId = getHeadingId(heading, index, claimedIds);

      item.className = `article-template__toc-item article-template__toc-item--${heading.tagName.toLowerCase()}`;
      link.href = `#${headingId}`;
      link.textContent = heading.textContent.trim();
      item.append(link);

      if (heading.tagName === 'H3' && latestH2Item) {
        if (!nestedList) {
          nestedList = document.createElement('ol');
          latestH2Item.append(nestedList);
        }
        nestedList.append(item);
      } else {
        list.append(item);
        latestH2Item = heading.tagName === 'H2' ? item : null;
        nestedList = null;
      }
    });

    registerToc(toc, button, tocContent, list);
  };

  const updateProgressBars = () => {
    progressFrame = null;

    document.querySelectorAll('[data-article-progress]').forEach((progressBar) => {
      const article = progressBar.closest('[data-article-enhancements]');
      const content = article?.querySelector('.article-template__content');
      const value = progressBar.querySelector('[data-article-progress-value]');

      if (!content || !value) return;

      const contentRect = content.getBoundingClientRect();
      const contentTop = contentRect.top + window.scrollY;
      const contentBottom = contentTop + contentRect.height;
      const progressEnd = contentBottom - window.innerHeight;
      let progress = 0;

      if (progressEnd <= contentTop) {
        progress = window.scrollY + window.innerHeight >= contentBottom ? 1 : 0;
      } else {
        progress = clamp((window.scrollY - contentTop) / (progressEnd - contentTop), 0, 1);
      }

      value.style.transform = `scaleX(${progress.toFixed(4)})`;
      progressBar.hidden = false;
    });
  };

  const scheduleProgressUpdate = () => {
    if (progressFrame !== null) return;
    progressFrame = window.requestAnimationFrame(updateProgressBars);
  };

  const init = (root = document) => {
    const tocs = [];

    if (root instanceof Element && root.matches('[data-article-toc]')) {
      tocs.push(root);
    }

    if ('querySelectorAll' in root) {
      tocs.push(...root.querySelectorAll('[data-article-toc]'));
    }

    tocs.forEach(buildToc);
    scheduleProgressUpdate();
  };

  window.ArticleEnhancements = { init };

  window.addEventListener('scroll', scheduleProgressUpdate, { passive: true });
  window.addEventListener('resize', scheduleProgressUpdate, { passive: true });
  window.addEventListener('load', scheduleProgressUpdate);

  const handleViewportChange = () => {
    tocInstances.forEach((instance) => {
      if (!instance.element.isConnected) {
        tocInstances.delete(instance);
        return;
      }

      applyTocViewportState(instance);
    });
    scheduleProgressUpdate();
  };

  if ('addEventListener' in mobileMedia) {
    mobileMedia.addEventListener('change', handleViewportChange);
  } else {
    mobileMedia.addListener(handleViewportChange);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    tocInstances.forEach((instance) => {
      if (!instance.element.isConnected || event.target.contains(instance.element)) {
        tocInstances.delete(instance);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
